import { useId, useState, type ReactNode } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { HACK_alignIconWithLabelBox } from "@privacy/ui";
import { Logo } from "../root/layout/Logo";
import { LogOutIcon, UserIcon } from "../shared/icons";
import { TrustUserInfoDialog } from "./TrustUserInfoDialog";
import { useTrustUserInfo } from "./TrustUserInfoProvider";
import { logoutMutationOptions } from "./trustAuthAPI";

const HEADER_HEIGHT = 54;

const AVATAR_SIZE = 25;
const TRUST_HEADER_TITLE = "Draftit Verify";

export const TrustPageHeader = () => {
  const { t } = useTranslation();
  const trustUserInfo = useTrustUserInfo();
  const logoutMutation = useMutation(logoutMutationOptions());
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUserInfoDialogOpen, setIsUserInfoDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const initials = `${trustUserInfo.firstName.charAt(0)}${trustUserInfo.lastName.charAt(0)}`;
  const fullName = `${trustUserInfo.firstName} ${trustUserInfo.lastName}`;
  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <Layout>
        <TrustLogo />
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <IconButton
            size="small"
            sx={{ "p": 0.25, "&:hover": { backgroundColor: theme => theme.palette.action.hover } }}
            aria-controls={open ? menuId : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-haspopup="true"
            aria-label={fullName}
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            <Avatar
              sx={{
                borderRadius: 1,
                bgcolor: theme => theme.palette.primary["100"],
                color: theme => theme.palette.primary.dark,
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                fontWeight: 600,
                fontSize: theme => theme.typography.pxToRem(12),
              }}
              alt={fullName}
            >
              {initials}
            </Avatar>
          </IconButton>

          <Menu
            id={menuId}
            anchorEl={anchorEl}
            disableScrollLock
            slotProps={{
              paper: {
                sx: { width: "250px" },
              },
            }}
            open={open}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                closeMenu();
                setIsUserInfoDialogOpen(true);
              }}
            >
              <UserIcon />
              <HACK_alignIconWithLabelBox>{t("Sidebar.MyAccount")}</HACK_alignIconWithLabelBox>
            </MenuItem>
            <MenuItem onClick={() => logoutMutation.mutate()}>
              <LogOutIcon />
              <HACK_alignIconWithLabelBox>{t("Sidebar.Logout")}</HACK_alignIconWithLabelBox>
            </MenuItem>
          </Menu>
        </Stack>
      </Layout>
      {isUserInfoDialogOpen && (
        <TrustUserInfoDialog
          userInfo={trustUserInfo}
          onCancel={() => setIsUserInfoDialogOpen(false)}
          onSaved={() => setIsUserInfoDialogOpen(false)}
        />
      )}
    </>
  );
};

const NotAuthenticated = () => {
  return (
    <Layout>
      <TrustLogo />
      <Box />
    </Layout>
  );
};

const TrustLogo = () => {
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", lineHeight: 0 }}>
        <Logo showText={false} />
      </Box>
      <Typography variant="h1" sx={{ fontSize: theme => theme.typography.pxToRem(24), fontWeight: 600 }}>
        {TRUST_HEADER_TITLE}
      </Typography>
    </Stack>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  return (
    <Box
      component="header"
      sx={{
        height: HEADER_HEIGHT,
        px: 2,
        borderBottom: "1px solid",
        borderColor: theme => theme.palette.divider,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: theme => theme.palette.grey[100],
      }}
    >
      {children}
    </Box>
  );
};

const Skeleton = () => <Layout>{null}</Layout>;

TrustPageHeader.NotAuthenticated = NotAuthenticated;
TrustPageHeader.Skeleton = Skeleton;
TrustPageHeader.HEIGHT = HEADER_HEIGHT;
